import { useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FiFileText,
    FiBarChart2,
    FiLayers,
    FiClock,
    FiList,
    FiShoppingCart,
    FiArchive,
    FiTrash2
} from "react-icons/fi";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { MdAutorenew, MdError } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { IoRestaurantOutline } from "react-icons/io5";
import { Bar, Pie } from "react-chartjs-2";
import { formatSalesValue } from "../../utils/formatUtils";
import WeeklyPurchaseChart from './WeeklyPurchaseChart';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    ArcElement
);

const valueLabelPlugin = {
    id: "valueLabelPlugin",

    afterDatasetsDraw(chart, args, pluginOptions) {
        const { ctx } = chart;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);

            meta.data.forEach((element, index) => {
                const value = dataset.data[index];
                if (value == null) return;

                const { x, y } = element.tooltipPosition();

                ctx.save();

                ctx.font = "600 11px sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";

                ctx.fillStyle = dataset.borderColor || "#333";

                // 👇 same formatting style as tooltip
                ctx.fillText(formatSalesValue(value), x, y - 6);

                ctx.restore();
            });
        });
    },
};

const PurchaseByOutletDept = ({ viewMode }) => {
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const userID = localStorage.getItem("userID");

    const today = new Date();
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    const [data, setData] = useState({});
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [deptColorMap, setDeptColorMap] = useState({});

    const colors = [
        "#FF6B6B",
        "#4ECDC4",
        "#FFD166",
        "#06D6A0",
        "#118AB2",
        "#EF476F",
        "#7209B7",
        "#3A86FF",
    ];

    const departmentPalette = [
        "#FF5A5F", // vivid coral
        "#F15BB5", // lively pink
        "#2EC4B6", // richer teal
        "#F9C74F", // deeper soft yellow
        "#48CAE4", // cool cyan
        "#F9844A", // warm orange
        "#3A86FF", // strong sky blue
        "#52B788", // fresh green
        "#6C5CE7", // bright indigo
        "#B089C6", // toned lavender
        "#E76F51", // terracotta
        "#43AA8B", // emerald teal
    ];

    const getDepartmentColorMap = (apiData) => {
        const allDepartments = new Set();

        Object.values(apiData).forEach((outlet) => {
            Object.keys(outlet.Departments).forEach((dept) => {
                const normalized = dept.trim().toLowerCase().replace(/\s+/g, " ");
                allDepartments.add(normalized);
            });
        });

        const map = {};
        Array.from(allDepartments).forEach((dept, index) => {
            map[dept] = departmentPalette[index % departmentPalette.length];
        });

        return map;
    };

    const formatDate = (date) =>
        date.toLocaleDateString('en-CA');

    const fetchData = async () => {
        try {
            setLoading(true);

            const response = await axios.post(
                `${baseUrl}/purchase-by-outlet-dept`,
                {
                    dateStart: formatDate(startDate),
                    dateEnd: formatDate(endDate),
                    token: token,
                    employee_id: Number(userID)
                }
            );

            const apiData = response.data;
            setData(apiData);

            const colorMap = getDepartmentColorMap(apiData);
            setDeptColorMap(colorMap);

            const labels = Object.keys(apiData);

            const values = labels.map(
                (outlet) => apiData[outlet].TotalPurchase
            );

            setChartData({
                labels,
                datasets: [
                    {
                        label: "Total Purchase",
                        data: values,
                        backgroundColor: labels.map(
                            (_, i) => colors[i % colors.length]
                        ),
                        borderRadius: 8,
                    },
                ],
            });
        } catch (error) {
            console.error("Error fetching purchase data:", error);
            setError(error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch purchase data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [token]);

    const buildPieData = (departments) => {
        const sorted = getSortedDepartments(departments);

        const labels = sorted.map(([dept]) => dept.trim().toLowerCase().replace(/\s+/g, " "));
        const values = sorted.map(([_, value]) => value);

        return {
            labels,
            datasets: [
                {
                    data: values,
                    backgroundColor: labels.map((dept) => deptColorMap[dept]),
                    borderWidth: 0.5,
                },
            ],
        };
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: "bottom",
                labels: {
                    generateLabels: (chart) => {
                        const data = chart.data;

                        return data.labels.map((label, i) => ({
                            text: label,
                            fillStyle: data.datasets[0].backgroundColor[i],
                            strokeStyle: data.datasets[0].backgroundColor[i],
                            index: i,

                            fontColor: "#4b5563",
                            color: "#4b5563",
                        }));
                    },
                },
            },
            tooltip: {
                callbacks: {
                    title: function (context) {
                        return context[0].dataset.label;
                    },
                    label: function (context) {
                        const outletName = context.dataset.label;
                        const outletData = data[outletName];

                        if (!outletData) return "";

                        const lines = [];

                        lines.push(
                            `Total: ${formatSalesValue(outletData.TotalPurchase)}`
                        );

                        lines.push(" ");

                        Object.entries(outletData.Departments).forEach(
                            ([dept, value]) => {
                                lines.push(
                                    `${dept.trim()}: ${formatSalesValue(value)}`
                                );
                            }
                        );

                        return lines;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Total Purchase",
                },
            },
            x: {
                title: {
                    display: true,
                    text: "Outlet",
                },
            },
        },
        layout: {
            padding: { top: 20 },
        },
    };

    const getButtonStyle = (color) => {
        const colors = {
            slate: "bg-gradient-to-r from-slate-400 to-slate-700",
            blue: "bg-gradient-to-r from-blue-400 to-blue-700",
            green: "bg-gradient-to-r from-green-400 to-green-700",
            yellow: "bg-gradient-to-r from-yellow-400 to-yellow-600",
            orange: "bg-gradient-to-r from-orange-400 to-orange-700",
            sky: "bg-gradient-to-r from-sky-400 to-sky-700",
            pink: "bg-gradient-to-r from-pink-400 to-pink-700",
            purple: "bg-gradient-to-r from-purple-400 to-purple-700",
            teal: "bg-gradient-to-r from-teal-400 to-teal-700",
            rose: "bg-gradient-to-r from-rose-400 to-rose-700",
        };

        return `
        ${colors[color]}
        text-white
        px-2.5 py-2
        text-sm font-semibold
        rounded-md
        flex items-center gap-1.5
        transition
        hover:scale-[1.03]
        hover:shadow-lg
        active:scale-95
    `;
    };

    const handleNavigate = (path) => {
        navigate(path, {
            state: { viewMode }
        });
    };

    const getSortedDepartments = (departments) => {
        return Object.entries(departments)
            .sort((a, b) => b[1] - a[1]);
    };

    const outletTotals = Object.entries(data)
        .map(([outlet, val]) => ({ outlet, total: val.TotalPurchase }))
        .sort((a, b) => b.total - a.total);

    const grandTotal = outletTotals.reduce((sum, o) => sum + o.total, 0);

    return (
        <div className="px-6 py-3 bg-gray-50 pb-12">
            <div className="mx-20 bg-white p-3 rounded-xl shadow-md mb-6 flex justify-between">
                <button
                    className={getButtonStyle("slate")}
                    onClick={() => handleNavigate('/detailedpurchasereport')}
                >
                    <FiFileText size={18} />
                    Purchase Report
                </button>

                <button
                    className={getButtonStyle("blue")}
                    onClick={() => handleNavigate('/statement')}
                >
                    <FiBarChart2 size={18} />
                    View Statement
                </button>

                {/* <button
                    className={getButtonStyle("green")}
                    onClick={() => handleNavigate('/store-reqs')}
                >
                    <FiLayers size={18} />
                    Requisition Center
                </button> */}

                <button
                    className={getButtonStyle("green")}
                    onClick={() => handleNavigate('/costcenter')}
                >
                    <FiLayers size={18} />
                    Cost Center
                </button>

                <button
                    className={getButtonStyle("yellow")}
                    onClick={() =>
                        handleNavigate(
                            '/central-kitchen/production-history'
                        )
                    }
                >
                    <IoRestaurantOutline size={18} />
                    Central Kitchen
                </button>

                <button
                    className={getButtonStyle("rose")}
                    onClick={() => handleNavigate('/wastage-report')}
                >
                    <FiTrash2 size={18} />
                    Wastage Report
                </button>

                <button
                    className={getButtonStyle("teal")}
                    onClick={() => handleNavigate('/items')}
                >
                    <FiClock size={18} />
                    Item History
                </button>

                <button
                    className={getButtonStyle("orange")}
                // onClick={() => handleNavigate('/item-expiry')}
                >
                    <FiList size={18} />
                    Expiry List
                </button>

                <button
                    className={getButtonStyle("sky")}
                // onClick={() => handleNavigate('/purchase-entry')}
                >
                    <FiShoppingCart size={18} />
                    Purchase Entry
                </button>

                <button
                    className={getButtonStyle("pink")}
                // onClick={() => handleNavigate('/order-history')}
                >
                    <FiArchive size={18} />
                    Purchase History
                </button>
            </div>

            {loading && (
                <div className="max-w-5xl mx-auto mt-4 mb-8 rounded-md bg-blue-50 p-4 bg-">
                    <div className="flex gap-3">
                        <MdAutorenew className="h-5 w-5 text-blue-500 animate-spin" />
                        <div className="text-blue-700">
                            Fetching purchase data...
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="max-w-5xl mx-auto mt-4 mb-8 rounded-md bg-red-50 p-4">
                    <div className="flex gap-3">
                        <MdError className="h-5 w-5 text-red-400" />
                        <div className="text-red-700">{error}</div>
                    </div>
                </div>
            )}

            {!loading && !error && (
                <div className="pb-8">
                    {/* ================== BAR CHART ================== */}
                    <div className="w-full px-20 mb-8 flex flex-col lg:flex-row gap-8">
                        {/* CHART */}
                        <div className="bg-white p-4 rounded-xl shadow-md flex-1 h-[450px]">
                            {chartData && <Bar data={chartData} options={options} plugins={[valueLabelPlugin]} />}
                        </div>

                        <div>
                            <div className="flex flex-col gap-3 mb-3">

                                {/* Start Date */}
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-semibold whitespace-nowrap w-16">
                                        Start Date
                                    </label>
                                    <div>
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date) => setStartDate(date)}
                                            dateFormat="yyyy-MM-dd"
                                            className="border px-2 py-1 text-sm rounded-md text-center w-32 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                    </div>
                                </div>

                                {/* End Date */}
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-semibold whitespace-nowrap w-16">
                                        End Date
                                    </label>
                                    <div>
                                        <DatePicker
                                            selected={endDate}
                                            onChange={(date) => setEndDate(date)}
                                            dateFormat="yyyy-MM-dd"
                                            className="border px-2 py-1 text-sm rounded-md text-center w-32 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={fetchData}
                                    className="px-4 py-2 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
                                >
                                    Search
                                </button>
                            </div>

                            {/* TABLE */}
                            <div className="bg-white p-4 rounded-xl shadow-md">
                                <div className="text-center font-semibold mb-3 text-lg">
                                    Purchase Summary
                                </div>

                                <table className="w-full text-sm border">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="border px-6 py-2 text-left">Outlet</th>
                                            <th className="border px-6 py-2 text-right">Total</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {outletTotals.map((row, i) => (
                                            <tr key={i} className="hover:bg-gray-50">
                                                <td className="border px-6 py-2 font-medium whitespace-nowrap">{row.outlet}</td>
                                                <td className="border px-6 py-2 text-right">
                                                    {formatSalesValue(row.total)}
                                                </td>
                                            </tr>
                                        ))}

                                        {/* GRAND TOTAL */}
                                        <tr className="bg-gray-200 font-semibold">
                                            <td className="border px-6 py-2 text-right">
                                                Grand Total
                                            </td>
                                            <td className="border px-6 py-2 text-right">
                                                {formatSalesValue(grandTotal)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center items-start gap-6">
                        {Object.entries(data).map(([outlet, outletData], index) => (
                            <div
                                key={index}
                                className="border rounded-lg p-4 shadow-md bg-white space-y-4 w-1/3"
                            >
                                <h2 className="text-lg font-semibold text-center">
                                    {outlet}
                                </h2>

                                {/* ================= PIE CHART ================= */}
                                <div className="flex justify-center items-center">
                                    <div className="w-[250px] h-[250px]">
                                        <Pie
                                            data={buildPieData(outletData.Departments)}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: false,
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: function (context) {
                                                                return `${context.label}: ${formatSalesValue(
                                                                    context.raw
                                                                )}`;
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* ================= TABLE ================= */}
                                <div>
                                    <table className="min-w-full border text-sm">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="border p-2 text-left">
                                                    Department
                                                </th>
                                                <th className="border p-2 text-right">
                                                    Amount
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getSortedDepartments(outletData.Departments).map(([dept, value], i) => (
                                                <tr
                                                    key={i}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td className="border p-2">
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="w-3 h-3 rounded-sm"
                                                                style={{
                                                                    backgroundColor: deptColorMap[
                                                                        dept.trim().toLowerCase().replace(/\s+/g, " ")
                                                                    ],
                                                                }}
                                                            />
                                                            {dept.trim()}
                                                        </div>
                                                    </td>
                                                    <td className="border p-2 text-right">
                                                        {formatSalesValue(value)}
                                                    </td>
                                                </tr>
                                            ))}

                                            <tr className="bg-gray-100 font-semibold">
                                                <td className="border p-2 text-right">
                                                    Total
                                                </td>
                                                <td className="border p-2 text-right">
                                                    {formatSalesValue(
                                                        outletData.TotalPurchase
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <WeeklyPurchaseChart baseUrl={baseUrl} token={token} userID={userID} />
        </div>
    );
};

export default PurchaseByOutletDept;
